import { z } from 'zod';
import { EXPORT_FORMATS, MAX_DOCUMENT_SIZE_BYTES, PAGINATION_MAX_PAGE_SIZE, REPORT_TYPES, ROLES, WEIGHING_SOURCES } from '../constants';

/**
 * Diese Schemas laufen auf BEIDEN Seiten: im Frontend für sofortiges
 * Formular-Feedback, im Backend als einzige Quelle der Wahrheit für die
 * tatsächliche Validierung (das Frontend-Ergebnis ist nur UX, nie
 * Sicherheitsgrenze - der Server prüft immer erneut mit demselben Schema).
 */

export const loginSchema = z.object({
  username: z.string().min(1, 'Benutzername erforderlich').max(64),
  password: z.string().min(1, 'Passwort erforderlich').max(256),
  rememberMe: z.boolean().optional().default(false),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const createUserSchema = z.object({
  username: z.string().min(3, 'Mindestens 3 Zeichen').max(64),
  password: z.string().min(10, 'Mindestens 10 Zeichen'),
  role: z.enum(ROLES),
});
export type CreateUserInput = z.infer<typeof createUserSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string().min(10, 'Mindestens 10 Zeichen'),
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

export const updateUserSchema = z.object({
  username: z.string().min(3).max(64).optional(),
  password: z.string().min(10).optional(),
  role: z.enum(ROLES).optional(),
  isActive: z.boolean().optional(),
});
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

const nameSchema = z.string().trim().min(1, 'Name erforderlich').max(120);

export const createMasterDataItemSchema = z.object({ name: nameSchema });
export type CreateMasterDataItemInput = z.infer<typeof createMasterDataItemSchema>;

export const updateMasterDataItemSchema = z.object({
  name: nameSchema.optional(),
  isActive: z.boolean().optional(),
});
export type UpdateMasterDataItemInput = z.infer<typeof updateMasterDataItemSchema>;

const plantCodeSchema = z
  .string()
  .trim()
  .min(1)
  .max(16)
  .regex(/^[A-Z0-9_-]+$/, 'Nur Großbuchstaben, Ziffern, "-" und "_"');

export const createPlantSchema = z.object({ name: nameSchema, code: plantCodeSchema });
export type CreatePlantInput = z.infer<typeof createPlantSchema>;

export const updatePlantSchema = z.object({ name: nameSchema.optional(), code: plantCodeSchema.optional() });
export type UpdatePlantInput = z.infer<typeof updatePlantSchema>;

export const createWeighingSchema = z.object({
  plantId: z.string().min(1),
  dateTime: z.string().datetime({ offset: true }),
  gateNumber: z.string().max(32).optional().nullable(),
  licensePlate: z.string().max(32).optional().nullable(),
  containerId: z.string().min(1),
  materialId: z.string().min(1),
  alloyId: z.string().min(1).optional().nullable(),
  driverId: z.string().min(1).optional().nullable(),
  /** Immer in kg - siehe `constants#kgToTonnes`. */
  netWeightKg: z.number().positive('Muss größer als 0 sein').max(100_000, 'Unrealistischer Wert'),
  remarks: z.string().max(2000).optional().nullable(),
  documentId: z.string().min(1).optional().nullable(),
  /** Vom Client gesetzt, wenn die Werte aus einem OCR-Scan stammen (siehe `OcrCapturePage`) - fehlt das Feld, bleibt es bei `MANUAL`. */
  source: z.enum(WEIGHING_SOURCES).optional(),
});
export type CreateWeighingInput = z.infer<typeof createWeighingSchema>;

export const updateWeighingSchema = createWeighingSchema.partial();
export type UpdateWeighingInput = z.infer<typeof updateWeighingSchema>;

export const weighingFilterSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(PAGINATION_MAX_PAGE_SIZE).default(50),
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
  plantId: z.string().optional(),
  containerId: z.string().optional(),
  materialId: z.string().optional(),
  alloyId: z.string().optional(),
  search: z.string().max(200).optional(),
});
export type WeighingFilterInput = z.infer<typeof weighingFilterSchema>;

export const pairTourSchema = z.object({
  weighingIds: z.array(z.string().min(1)).min(2).max(2, 'Eine Tour besteht aus genau zwei Wiegungen'),
});
export type PairTourInput = z.infer<typeof pairTourSchema>;

export const createCalendarEventSchema = z.object({
  date: z.string().date(),
  title: z.string().min(1).max(200),
  description: z.string().max(2000).optional().nullable(),
  isReminder: z.boolean().default(false),
  reminderTime: z.string().regex(/^\d{2}:\d{2}$/).optional().nullable(),
});
export type CreateCalendarEventInput = z.infer<typeof createCalendarEventSchema>;

export const updateCalendarEventSchema = createCalendarEventSchema.partial();
export type UpdateCalendarEventInput = z.infer<typeof updateCalendarEventSchema>;

/** Container-Kippung - eigenständiges Modul, nicht mehr an einen Kalender-Termin gekoppelt. */
export const createContainerDumpSchema = z.object({
  containerOneId: z.string().min(1),
  containerTwoId: z.string().min(1).optional().nullable(),
  materialId: z.string().min(1),
  alloyId: z.string().min(1).optional().nullable(),
  dumpDate: z.string().date(),
  dumpTime: z.string().regex(/^\d{2}:\d{2}$/),
});
export type CreateContainerDumpInput = z.infer<typeof createContainerDumpSchema>;

export const updateContainerDumpSchema = createContainerDumpSchema.partial();
export type UpdateContainerDumpInput = z.infer<typeof updateContainerDumpSchema>;

export const containerDumpFilterSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(PAGINATION_MAX_PAGE_SIZE).default(50),
  from: z.string().date().optional(),
  to: z.string().date().optional(),
  containerId: z.string().optional(),
  materialId: z.string().optional(),
  alloyId: z.string().optional(),
  search: z.string().max(200).optional(),
});
export type ContainerDumpFilterInput = z.infer<typeof containerDumpFilterSchema>;

export const createVehicleLogSchema = z.object({
  vehicleCode: z.string().min(1).max(32),
  logDate: z.string().date(),
  startTime: z.string().regex(/^\d{2}:\d{2}$/),
  endTime: z.string().regex(/^\d{2}:\d{2}$/),
  kilometers: z.number().min(0).max(2000),
  kwhConsumed: z.number().min(0).max(500),
});
export type CreateVehicleLogInput = z.infer<typeof createVehicleLogSchema>;

export const updateVehicleLogSchema = createVehicleLogSchema.partial();
export type UpdateVehicleLogInput = z.infer<typeof updateVehicleLogSchema>;

export const exportRequestSchema = z.object({
  format: z.enum(EXPORT_FORMATS),
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
  search: z.string().max(200).optional(),
});
export type ExportRequestInput = z.infer<typeof exportRequestSchema>;

export const reportRequestSchema = z.object({
  type: z.enum(REPORT_TYPES),
  date: z.string().date(),
  plantId: z.string().optional(),
});
export type ReportRequestInput = z.infer<typeof reportRequestSchema>;

/** Nur zur Anzeige im Frontend (Größenlimit) - die eigentliche, sicherheitsrelevante Prüfung läuft im Backend per Magic-Bytes. */
export const documentSizeSchema = z.number().int().positive().max(MAX_DOCUMENT_SIZE_BYTES);
